import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, X, Zap } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LookalikeModal({ open, onClose, seedIds }) {
  const { t, lang } = useLang();
  const [locations, setLocations] = useState("");
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [savingId, setSavingId] = useState(null);

  if (!open) return null;

  const run = async (e) => {
    e?.preventDefault?.();
    setRunning(true); setData(null);
    try {
      const target_locations = locations.split(/\n|;|\|/).map((s) => s.trim()).filter(Boolean);
      const { data: r } = await axios.post(`${API}/lookalike-leads`, {
        seed_lead_ids: seedIds, target_locations, max_per_query: 8, lang,
      });
      setData(r);
      toast.success(t("look.found", { n: r.candidates?.length || 0 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("look.failed"));
    } finally { setRunning(false); }
  };

  const saveCandidate = async (ld) => {
    setSavingId(ld.id || ld.place_id);
    try {
      const { data: r } = await axios.post(`${API}/save-lead`, ld);
      if (r.deduped) toast.warning(t("look.already_saved"));
      else toast.success(t("look.saved", { name: ld.name }));
      // Mark this candidate as saved locally
      setData((d) => d ? { ...d, candidates: d.candidates.filter((c) => (c.id || c.place_id) !== (ld.id || ld.place_id)) } : d);
    } catch (e) {
      toast.error(t("look.save_failed"));
    } finally { setSavingId(null); }
  };

  return (
    <div data-testid="lookalike-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="al-card max-w-[820px] w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#A855F7]" />
            <div>
              <h2 className="text-[16px] font-bold text-[#111827]">{t("look.modal_title")}</h2>
              <p className="text-[12px] text-[#6B7280] mt-0.5">{t("look.modal_subtitle", { n: seedIds.length })}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]" data-testid="lookalike-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={run} className="space-y-3 mb-4">
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1">
              {t("look.locations_label")}
            </label>
            <textarea
              data-testid="lookalike-locations-input"
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-[13px] font-mono bg-white focus:outline-none focus:border-[#A855F7]"
              placeholder={t("look.locations_placeholder")}
              value={locations}
              onChange={(e) => setLocations(e.target.value)}
            />
            <p className="text-[10.5px] text-[#9CA3AF] mt-1">{t("look.locations_hint")}</p>
          </div>
          <button
            type="submit"
            data-testid="lookalike-run-btn"
            disabled={running || seedIds.length === 0}
            className="al-btn-primary flex items-center justify-center gap-2 w-full h-10 rounded-lg text-[13px] font-semibold disabled:opacity-60"
            style={{ background: "#A855F7" }}
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {t("look.run_button")}
          </button>
        </form>

        {data && (
          <div data-testid="lookalike-results">
            <div className="bg-[#F3E8FF] border border-[#DDD6FE] rounded-lg p-3 mb-3">
              <div className="text-[12px] font-semibold text-[#6B21A8] mb-1">
                {t("look.queries_count", { n: (data.queries || []).length })}
              </div>
              <ul className="space-y-1 text-[11.5px] text-[#7C3AED]">
                {(data.queries || []).map((q, i) => (
                  <li key={`${q.sector || "q"}-${q.keyword || i}`} data-testid={`lookalike-query-${i}`}>
                    • <strong>{q.sector}</strong>{q.keyword ? ` · ${q.keyword}` : ""} <span className="italic text-[#9333EA]"> — {q.rationale_short || ""}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[10.5px] text-[#7C3AED] mt-2">{t("look.locations_used")}: {(data.locations || []).join(", ")}</div>
            </div>

            <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#6B7280] mb-2">
              {t("look.candidates_title", { n: (data.candidates || []).length })}
            </h3>
            {(data.candidates || []).length === 0 ? (
              <p className="text-[12px] text-[#9CA3AF] py-4 text-center italic">{t("look.no_candidates")}</p>
            ) : (
              <div className="space-y-1.5">
                {data.candidates.map((c, i) => {
                  const cKey = c.id || c.place_id || `${c.name}-${i}`;
                  const isSaving = savingId === cKey;
                  return (
                    <div
                      key={cKey}
                      data-testid={`lookalike-candidate-${i}`}
                      className="border border-[#E5E7EB] rounded-lg p-2.5 flex items-center gap-3 hover:bg-[#FAFAFB] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[12.5px] text-[#111827] truncate">{c.name}</div>
                        <div className="text-[11px] text-[#6B7280] truncate">{c.address}</div>
                        <div className="text-[10.5px] text-[#9CA3AF] truncate">{c.sector} · {c._lookalike_location}</div>
                      </div>
                      <span className={
                        "text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 " +
                        (c.badge === "QUENTE" ? "bg-[#FEE2E2] text-[#DC2626]" :
                         c.badge === "FRIO"   ? "bg-[#DCFCE7] text-[#16A34A]" :
                         "bg-[#FFEDD5] text-[#C2410C]")
                      }>{c.badge || "MORNO"}</span>
                      <span className="text-[12px] font-bold text-[#111827] tabular-nums w-10 text-right">{c.digital_score ?? "—"}</span>
                      <button
                        type="button"
                        data-testid={`lookalike-save-${i}`}
                        onClick={() => saveCandidate(c)}
                        disabled={isSaving}
                        className="al-btn-primary text-[11px] h-7 px-2.5 rounded-md disabled:opacity-60"
                      >
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : t("look.save_btn")}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
